import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetSegmentInfoQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import {
  selectComparison,
  selectComparisonEndDate,
  selectCurrentProjectInfo,
  selectSegmentId,
  selectSlowdownThreshold,
  selectTargetDate,
  selectTargetEndDate,
} from 'state/workflowSlice';

import dayjs from 'dayjs';
import {
  ChartDataExpander,
  isMultiDayChart,
} from 'features/chart/ChartDataExpander';
import { speedChartOptions } from 'features/chart/speedChartOptions';
import { useRouteInfo } from 'features/common/hooks/useRouteInfo';
import { convertSpeeds, formatSpeed, speedUnit } from 'features/common/utils';
import { useTargetDateMetrics } from 'features/workflow_traveltime/nrtTravelTimeCommon';
import Highcharts from 'highcharts';

import { SyncedHighchartsReact } from './SyncedHighchartsReact';
import {
  SeriesGroupType,
  dayMarkerLineBase,
  formatTargetSeriesName,
  formatTypicalSeriesName,
  makeFormatTooltip,
} from './nrtChartCommon';
import { useChartResize } from './useChartResize';

import 'features/chart/Chart.css';

import styles from './Chart.module.css';

export function SpeedChart() {
  const targetDate: string = useAppSelector(selectTargetDate);
  const targetEndDate = useAppSelector(selectTargetEndDate);
  const comparisonEndDate = useAppSelector(selectComparisonEndDate);
  const segmentId: string = useAppSelector(selectSegmentId);
  const slowdownThreshold = useAppSelector(selectSlowdownThreshold);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const comparison = useAppSelector(selectComparison);

  const chartComponent = useRef(null); // so we can call reflow

  const [chartOptions, setChartOptions] = useState(speedChartOptions as any);
  const { basicRouteInfo } = useRouteInfo();
  useChartResize(chartComponent);

  // speedData from REST api
  const { metrics, comparisonData: comparisonSpeedData } = useTargetDateMetrics(
    false,
    0,
  );

  // segmentData layer from REST api
  const { layer } = useContext(LayerContext);
  const { currentData: segmentData } = useGetSegmentInfoQuery(
    { layerId: layer, routeIds: basicRouteInfo?.realizedSegments },
    {
      skip:
        !layer ||
        !segmentId ||
        !basicRouteInfo ||
        !(basicRouteInfo.realizedSegments?.length > 0),
    },
  );
  // memoize chartDataExpander
  const chartDataExpander = useMemo(
    () =>
      targetDate &&
      new ChartDataExpander(
        targetDate,
        undefined,
        targetEndDate,
        comparisonEndDate,
      ),
    [targetDate, targetEndDate, comparisonEndDate],
  );

  // memoize segmentProperties
  const segmentProperties = useMemo(
    () => segmentData && segmentData?.segments[segmentId]?.properties,
    [segmentData, segmentId],
  );

  // Hold ref to old data so we can keep it alive while the new graph is loading
  const segmentSpeedsRef = useRef(undefined);
  // memoize expanded segmentSpeeds
  const segmentSpeeds = useMemo(() => {
    const value = chartDataExpander?.expandSpeeds(
      metrics,
      userProject.uses_metric,
    );
    if (value) {
      segmentSpeedsRef.current = { value, date: targetDate, seg: segmentId };
      return value;
    } else if (
      segmentSpeedsRef.current?.date === targetDate &&
      segmentSpeedsRef.current?.seg === segmentId
    ) {
      return segmentSpeedsRef.current.value;
    }
    return undefined;
  }, [metrics, chartDataExpander, targetDate, userProject]);
  // console.log(`memoized segmentSpeeds ${JSON.stringify(segmentSpeeds)}`);
  const comparisonSpeedsRef = useRef(undefined);
  // memoize comparisonSpeeds
  const comparisonSpeeds = useMemo(() => {
    if (comparison) {
      const comparisonChartDataExpander = new ChartDataExpander(
        targetDate,
        comparison,
        targetEndDate,
        comparisonEndDate,
      );
      const value = comparisonChartDataExpander.expandSpeeds(
        comparisonSpeedData,
        userProject.uses_metric,
      );
      if (value) {
        comparisonSpeedsRef.current = {
          value,
          date: targetDate,
          seg: segmentId,
        };
        return value;
      } else if (
        comparisonSpeedsRef.current?.date === targetDate &&
        comparisonSpeedsRef.current?.seg === segmentId
      ) {
        return comparisonSpeedsRef.current.value;
      }
    }
    return undefined;
  }, [
    comparison,
    comparisonEndDate,
    comparisonSpeedData,
    chartDataExpander,
    targetDate,
    targetEndDate,
    userProject,
  ]);
  // console.log(`###  memoized comparisonSpeeds ${JSON.stringify(comparisonSpeeds)}`);

  // memoize thresholds
  const slowdownBands = useMemo(
    () =>
      chartDataExpander?.slowdownBands(
        segmentSpeeds,
        comparisonSpeeds,
        slowdownThreshold,
      ),
    [segmentSpeeds, comparisonSpeeds, slowdownThreshold, chartDataExpander],
  );

  const freeflowSpeed = useMemo(() => {
    return convertSpeeds(
      segmentProperties?.freeflow_speed_mph,
      userProject.uses_metric,
    );
  }, [segmentProperties]);

  // memoize freeflowSpeedFlatline
  const freeflowSpeedFlatline = useMemo(() => {
    // Hide the flatline until segment speeds data has loaded
    if (segmentSpeedsRef.current?.date === targetDate) {
      return chartDataExpander?.flatline(freeflowSpeed);
    } else {
      return [];
    }
  }, [freeflowSpeed, chartDataExpander, segmentSpeeds]);

  // update chart with new targetDate, freeflowSpeeds, segmentSpeeds
  useEffect(() => {
    const nrtMultiDay = isMultiDayChart(
      targetDate,
      targetEndDate,
      comparison,
      comparisonEndDate,
    );
    const dayStart = dayjs(targetDate).startOf('day');
    const dayEnd = dayStart.add(1, 'day');
    const xAxisOptions: any = nrtMultiDay
      ? {
          plotBands: slowdownBands,
          min: dayStart.valueOf(),
          max: dayEnd.valueOf(),
          startOnTick: true,
          endOnTick: true,
          plotLines: [
            { ...dayMarkerLineBase, value: dayStart.valueOf() },
            { ...dayMarkerLineBase, value: dayEnd.valueOf() },
          ],
        }
      : {
          plotBands: slowdownBands,
          min: null,
          max: null,
          plotLines: [
            { ...dayMarkerLineBase, value: dayjs(targetDate) },
            { ...dayMarkerLineBase, value: dayjs(targetDate).add(1, 'day') },
          ],
        };

    setChartOptions({
      xAxis: xAxisOptions,
      yAxis: {
        title: {
          text: `Average Speed (${speedUnit(userProject.uses_metric)})`,
        },
      },
      series: [
        {
          id: 'freeflow',
          data: freeflowSpeedFlatline,
          marker: { enabled: false },
        },
        {
          id: 'typical_speed',
          name: formatTypicalSeriesName(
            comparison,
            targetDate,
            userProject?.uses_middle_east_day_types,
            false,
            undefined,
            false,
            comparisonEndDate,
          ),
          data: comparisonSpeeds || [],
          marker: { enabled: false },
        },
        {
          id: 'segment_speed',
          data: segmentSpeeds || [],
          name: formatTargetSeriesName(targetDate, false, targetEndDate),
          marker: { enabled: false },
        },
      ],
      tooltip: {
        formatter: makeFormatTooltip(
          slowdownBands,
          metrics?.bin_size,
          userProject?.uses_middle_east_day_types,
          (v) => `${formatSpeed(v, userProject.uses_metric, true)}`,
          targetDate,
          comparison,
          {
            [SeriesGroupType.Nrt]: ['segment_speed'],
            [SeriesGroupType.Comparison]: ['typical_speed'],
          },
          freeflowSpeed,
          undefined,
          targetEndDate,
          comparisonEndDate,
        ),
      },
    });
  }, [
    segmentSpeeds,
    comparisonSpeeds,
    freeflowSpeedFlatline,
    slowdownBands,
    metrics,
    targetDate,
    targetEndDate,
    comparison,
    comparisonEndDate,
    userProject?.uses_metric,
  ]);

  return (
    <div className={styles.chart}>
      <SyncedHighchartsReact
        ref={chartComponent}
        highcharts={Highcharts}
        containerProps={{ style: { height: '100%', width: '100%' } }}
        options={chartOptions}
      />
    </div>
  );
}
